import type { ReactNode } from "react";
import { CCard, CCardBody } from "@coreui/react-pro";
import { ScryerSpark } from "@scryerlabs/ui";

interface FailureCardProps {
  title: string;
  message: ReactNode;
  /**
   * The trace id for the failure, when the server sent one. Takes the place of `details`, because a
   * reference support can resolve beats a sentence the member has already read above it.
   */
  reference?: string | null;
  /** Diagnostic text shown below a rule. Safe to render — never server-supplied exception text. */
  details?: string | null;
  /** Buttons. Rendered above the rule. */
  children?: ReactNode;
}

/**
 * The full-screen "we could not do the thing" card. One component rather than three so the
 * provisioning failure, a render crash and a failed startup do not drift into three designs of the
 * same moment.
 */
export function FailureCard({ title, message, reference, details, children }: FailureCardProps) {
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-body p-3">
      <CCard className="text-center shadow-sm" style={{ maxWidth: "24rem", width: "100%" }}>
        <CCardBody className="p-4">
          <ScryerSpark size={40} className="mb-3" />
          <h1 className="h5 mb-2">{title}</h1>
          <p className="text-body-secondary mb-4">{message}</p>
          {children}
          {reference ? (
            <div className="text-body-secondary small mt-4 pt-3 border-top">
              <div className="mb-1">If you contact us, quote this reference:</div>
              {/* user-select-all so one click selects the whole id — it is long, and a member
                  half-copying it gives support something that resolves to nothing. */}
              <code className="user-select-all text-body">{reference}</code>
            </div>
          ) : (
            details && (
              <div className="text-body-secondary small mt-4 pt-3 border-top">
                Details: <code>{details}</code>
              </div>
            )
          )}
        </CCardBody>
      </CCard>
    </div>
  );
}
